import { useMemo } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, useLocation } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import "@reframe-mobile/index.css";
import { Toaster } from "sonner";
import AppRoutes from "@reframe-mobile/app/routes";
import { ModalProvider } from "@reframe-mobile/features/modal";
import { listenForSwUpdate } from "@reframe-mobile/lib/sw-update";
import { PatchPilotProvider } from "@repo/patch-pilot-sdk/react";
import { useAuthStore } from "@reframe-mobile/features/auth/store";
import { Config } from "@reframe-mobile/config/env";
import { SentryFallback } from "@reframe-mobile/components/ui/sentry-fallback";
import { initSentry } from "@reframe-mobile/lib/sentry";

// theme store import triggers initial theme setup
import "@reframe-mobile/features/theme/store";

/** FAB과 동일한 bottom 위치: 56px(탭바) + 16px(간격) + safe-area-inset-bottom */
function useFabOffset() {
  return useMemo(() => {
    const safeArea = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        "--safe-area-inset-bottom",
      ),
      10,
    );
    return { bottom: 56 + 16 + (safeArea || 0) };
  }, []);
}

function PatchPilotConnector() {
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const userInfo = useMemo(
    () => (user ? { email: user.email } : undefined),
    [user?.email],
  );
  const offset = useFabOffset();

  if (!pathname.startsWith("/more")) return null;

  return (
    <PatchPilotProvider
      apiKey={Config.PATCH_PILOT_API_KEY}
      apiUrl={Config.PATCH_PILOT_API_URL}
      user={userInfo}
      offset={offset}
      theme="auto"
      locale="ko"
    />
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

const el = document.getElementById("root");
if (el) {
  initSentry();
  const root = createRoot(el);
  root.render(
    <Sentry.ErrorBoundary fallback={SentryFallback}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRoutes />
          <Toaster
            position="bottom-center"
            expand={false}
            visibleToasts={3}
            gap={8}
            toastOptions={{
              unstyled: true,
            }}
          />
          <ModalProvider />
          <PatchPilotConnector />
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>,
  );
  listenForSwUpdate();
} else {
  throw new Error("Could not find root element");
}
